import React, { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { PlusIcon, XMarkIcon } from '@heroicons/react/16/solid';
import { Button } from '../common/Catalyst/button';
import { AmenityHoursWindow, AmenityReturn } from '../clients/rust-api-client';
import { amenityService } from '../clients/ApiClients';
import {
  WEEKDAYS,
  formatMinute,
  reservationErrorMessage,
  windowProblem,
  windowsFor,
} from '../reservation/reservationMeta';
import { helpClass } from '../reservation/formStyles';
import { ErrorAlert } from '../common/Custom/ErrorAlert';
import { FieldPicker } from '../common/Custom/FieldPicker';

/**
 * The amenity's weekly opening hours (#642): any number of windows per
 * weekday — a pool closed for lunch is two — saved as one replacement
 * set. A day with no windows is closed. "Midnight" at the end of a window
 * is the end of the day (minute 1440), so a court open until midnight
 * does not wrap into the next morning.
 *
 * Phase 2: an amenity with seasons (Seasons tab) has one schedule per
 * season plus the default one, picked with the switch above the week. A
 * season with no hours of its own uses the default hours; outside every
 * season the default hours apply, and with none the amenity is closed.
 * All of them are saved together, as the one replacement set the API
 * takes.
 */
interface Props {
  amenity: AmenityReturn;
  readOnly: boolean;
  onSaved: (amenity: AmenityReturn) => void;
}

const optionsFor = (step: number, includeMidnight: boolean): number[] => {
  const out: number[] = [];
  for (let m = 0; m < 1440; m += step) out.push(m);
  if (includeMidnight) out.push(1440);
  return out;
};

/** The windows of one schedule: a season's, or the default (null). */
export const forSchedule = (all: AmenityHoursWindow[], seasonId: string | null): AmenityHoursWindow[] =>
  all.filter((w) => (w.season_id ?? null) === seasonId);

export const AmenityHoursEditor: React.FC<Props> = ({ amenity, readOnly, onSaved }) => {
  const [windows, setWindows] = useState<AmenityHoursWindow[]>(amenity.hours);
  const [seasonId, setSeasonId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const seasons = useMemo(() => amenity.seasons ?? [], [amenity.seasons]);

  useEffect(() => setWindows(amenity.hours), [amenity.hours]);
  useEffect(() => {
    if (seasonId && !seasons.some((s) => s.id === seasonId)) setSeasonId(null);
  }, [seasons, seasonId]);

  const scoped = useMemo(() => forSchedule(windows, seasonId), [windows, seasonId]);

  // Every 15 minutes, or the amenity's own increment when that is finer.
  const step = Math.min(15, amenity.slot_increment_minutes || 15);
  const opens = useMemo(
    () => optionsFor(step, false).map((m) => ({ value: String(m), label: formatMinute(m) })),
    [step]
  );
  const closes = useMemo(
    () =>
      optionsFor(step, true)
        .filter((m) => m > 0)
        .map((m) => ({ value: String(m), label: m === 1440 ? 'midnight (end of day)' : formatMinute(m) })),
    [step]
  );

  const problems = WEEKDAYS.map((_, d) => windowProblem(windowsFor(scoped, d)));
  // Every schedule must be valid to save, not only the one on screen.
  const valid = [null, ...seasons.map((s) => s.id)].every((id) =>
    WEEKDAYS.every((_, d) => windowProblem(windowsFor(forSchedule(windows, id), d)) === null)
  );

  const update = (day: number, index: number, patch: Partial<AmenityHoursWindow>) =>
    setWindows((all) => {
      const target = windowsFor(forSchedule(all, seasonId), day)[index];
      return all.map((w) => (w === target ? { ...w, ...patch } : w));
    });
  const remove = (day: number, index: number) =>
    setWindows((all) => {
      const target = windowsFor(forSchedule(all, seasonId), day)[index];
      return all.filter((w) => w !== target);
    });
  const add = (day: number) =>
    setWindows((all) => {
      const last = windowsFor(forSchedule(all, seasonId), day).slice(-1)[0];
      const open = last ? Math.min(last.close_minute + 60, 1380) : 9 * 60;
      return [
        ...all,
        { weekday: day, open_minute: open, close_minute: Math.min(open + 8 * 60, 1440), season_id: seasonId },
      ];
    });
  const copyToAll = (day: number) =>
    setWindows((all) => {
      const source = windowsFor(forSchedule(all, seasonId), day);
      const others = all.filter((w) => (w.season_id ?? null) !== seasonId);
      return [
        ...others,
        ...WEEKDAYS.flatMap((_, d) =>
          source.map((w) => ({
            weekday: d,
            open_minute: w.open_minute,
            close_minute: w.close_minute,
            season_id: seasonId,
          }))
        ),
      ];
    });
  const season = seasons.find((s) => s.id === seasonId) ?? null;

  const save = async () => {
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      await amenityService.replaceAmenityHours({
        amenityId: amenity.id,
        amenityHoursReplace: { windows },
      });
      onSaved((await amenityService.getAmenity({ amenityId: amenity.id })).data);
      setSaved(true);
    } catch (err) {
      setError(reservationErrorMessage(err, "Couldn't save the opening hours."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-4">
      {seasons.length > 0 && (
        <div className="space-y-1.5">
          <div role="group" aria-label="Which schedule" className="flex flex-wrap items-center gap-0.5 rounded-lg border border-line p-0.5 text-[12px]">
            {[{ id: null as string | null, name: 'Default hours' }, ...seasons].map((s) => (
              <button
                key={s.id ?? 'default'}
                type="button"
                aria-pressed={seasonId === s.id}
                onClick={() => setSeasonId(s.id)}
                className={clsx(
                  'rounded-md px-2.5 py-1 font-medium transition-colors',
                  seasonId === s.id ? 'bg-fg/10 text-fg' : 'text-fg-muted hover:text-fg'
                )}
              >
                {s.name}
              </button>
            ))}
          </div>
          <p className={helpClass}>
            {season
              ? scoped.length === 0
                ? `${season.name} has no hours of its own, so the default hours apply during it.`
                : `These hours apply during ${season.name}.`
              : forSchedule(windows, null).length === 0
                ? 'No default hours: the amenity is closed outside its seasons.'
                : 'The default hours apply outside every season, and during a season with none of its own.'}
          </p>
        </div>
      )}
      <p className={helpClass}>Times in {amenity.timezone}. A day with no hours is closed.</p>
      <ul className="divide-y divide-line/60 rounded-lg border border-line">
        {WEEKDAYS.map((name, day) => {
          const forDay = windowsFor(scoped, day);
          return (
            <li key={name} className="flex flex-wrap items-start gap-3 px-3 py-2.5">
              <span className="w-24 pt-1 text-[13px] font-medium text-fg-secondary">{name}</span>
              <div className="min-w-0 flex-1 space-y-1.5">
                {forDay.length === 0 && <p className="pt-1 text-[13px] text-fg-muted">Closed</p>}
                {forDay.map((w, i) => (
                  <div key={`${w.open_minute}-${i}`} className="flex items-center gap-2 text-[13px]">
                    {readOnly ? (
                      <span className="text-fg-secondary">
                        {formatMinute(w.open_minute)} – {formatMinute(w.close_minute)}
                      </span>
                    ) : (
                      <>
                        <FieldPicker
                          size="sm"
                          className="w-28"
                          label={`${name} window ${i + 1} opens`}
                          value={String(w.open_minute)}
                          onChange={(v) => update(day, i, { open_minute: Number(v) })}
                          options={opens}
                        />
                        <span className="text-fg-muted">to</span>
                        <FieldPicker
                          size="sm"
                          className="w-48"
                          label={`${name} window ${i + 1} closes`}
                          value={String(w.close_minute)}
                          onChange={(v) => update(day, i, { close_minute: Number(v) })}
                          options={closes}
                        />
                        <button
                          type="button"
                          onClick={() => remove(day, i)}
                          aria-label={`Remove ${name} window ${i + 1}`}
                          className="rounded p-1 text-fg-muted hover:bg-fg/5 hover:text-fg"
                        >
                          <XMarkIcon className="h-3.5 w-3.5" />
                        </button>
                      </>
                    )}
                  </div>
                ))}
                {problems[day] && <p className="text-[12px] text-danger">{problems[day]}</p>}
              </div>
              {!readOnly && (
                <div className="flex items-center gap-2 pt-0.5">
                  <button
                    type="button"
                    onClick={() => add(day)}
                    className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[12px] text-fg-muted hover:bg-fg/5 hover:text-fg"
                  >
                    <PlusIcon className="h-3 w-3" aria-hidden /> Add hours
                  </button>
                  {forDay.length > 0 && (
                    <button
                      type="button"
                      onClick={() => copyToAll(day)}
                      className="rounded px-1.5 py-0.5 text-[12px] text-fg-muted hover:bg-fg/5 hover:text-fg"
                    >
                      Copy to every day
                    </button>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
      <ErrorAlert>{error}</ErrorAlert>
      {!readOnly && (
        <div className="flex items-center gap-3">
          <Button color="sky" onClick={save} disabled={!valid || saving}>
            {saving ? 'Saving…' : 'Save hours'}
          </Button>
          {saved && <span className={clsx(helpClass)}>Saved.</span>}
        </div>
      )}
    </div>
  );
};

export default AmenityHoursEditor;
